<script>
import PacientController from "@/controller/PacientController";

export default {
  props: {
    pacientId: { type: Number, required: true },
  },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
      filters: {
        date_ini: "",
        date_end: "",
        search: "",
      },
      currentPage: 1,
      perPage: 20,
    };
  },
  computed: {
    summary() {
      return this.data?.summary || {};
    },
    atendimentos() {
      return this.data?.atendimentos || [];
    },
    evolutions() {
      return this.data?.evolutions?.data || [];
    },
    evolutionTotal() {
      return this.data?.evolutions?.total || 0;
    },
    attendanceByMonth() {
      return this.data?.attendance?.by_month || [];
    },
    files() {
      return this.data?.files || [];
    },
  },
  created() {
    this.PacientController = new PacientController();
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData(page = this.currentPage) {
      this.loading = true;
      this.error = null;
      this.currentPage = page;
      try {
        const payload = await this.PacientController.getProntuario(this.pacientId, {
          evolutions_page: this.currentPage,
          evolutions_per_page: this.perPage,
          ...this.filters,
        });
        this.data = payload;
      } catch (err) {
        this.error = err?.response?.data?.message || "Não foi possível carregar o prontuário.";
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      await this.fetchData(1);
    },
    resetFilters() {
      this.filters = { date_ini: "", date_end: "", search: "" };
      this.fetchData(1);
    },
  },
};
</script>

<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <b-spinner class="align-middle mr-2"></b-spinner>
      <strong>Carregando prontuário...</strong>
    </div>

    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <div v-else-if="data">
      <section class="mb-3 p-3 border rounded">
        <h5 class="mb-3">Resumo clínico</h5>
        <div class="row">
          <div class="col-md-3 col-sm-6 mb-2"><strong>Atendimentos:</strong> {{ summary.atendimentos_total || 0 }}</div>
          <div class="col-md-3 col-sm-6 mb-2"><strong>Sessões realizadas:</strong> {{ summary.sessions_done || 0 }}</div>
          <div class="col-md-3 col-sm-6 mb-2"><strong>Sessões futuras:</strong> {{ summary.sessions_upcoming || 0 }}</div>
          <div class="col-md-3 col-sm-6 mb-2"><strong>Faltas paciente:</strong> {{ summary.absences_patient || 0 }}</div>
        </div>
      </section>

      <section class="mb-3">
        <h5 class="mb-3">Planos de atendimento</h5>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Especialidade</th>
                <th>Profissional</th>
                <th>Abordagem</th>
                <th>Convênio</th>
                <th>Período</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!atendimentos.length">
                <td colspan="5" class="text-center">Nenhum atendimento encontrado</td>
              </tr>
              <tr v-for="item in atendimentos" :key="item.id">
                <td>{{ item.specialty?.name || "-" }}</td>
                <td>{{ item.professional?.name || "-" }}</td>
                <td>{{ item.approach?.name || "-" }}</td>
                <td>{{ item.agreement?.name || "-" }}</td>
                <td>{{ item.start_date || "-" }} - {{ item.end_date || "atual" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="mb-3">
        <h5 class="mb-3">Evoluções clínicas</h5>
        <div class="row mb-2">
          <div class="col-md-3 mb-2"><b-form-input v-model="filters.date_ini" type="date"></b-form-input></div>
          <div class="col-md-3 mb-2"><b-form-input v-model="filters.date_end" type="date"></b-form-input></div>
          <div class="col-md-4 mb-2"><b-form-input v-model="filters.search" placeholder="Buscar no parecer"></b-form-input></div>
          <div class="col-md-2 mb-2 text-right">
            <b-button size="sm" variant="primary" class="mr-1" @click="applyFilters">Filtrar</b-button>
            <b-button size="sm" variant="secondary" @click="resetFilters">Limpar</b-button>
          </div>
        </div>

        <div v-if="!evolutions.length" class="alert alert-light border">
          Sem evoluções registradas no período.
        </div>

        <div v-for="evo in evolutions" :key="evo.id" class="border rounded p-3 mb-2 evolution-card">
          <div class="d-flex justify-content-between flex-wrap">
            <strong>{{ formatDateTimeBR(evo.start) }} {{ evo.start_time || "" }}</strong>
            <div>
              <b-badge v-if="evo.is_cancelled" variant="danger" class="mr-1">Cancelada</b-badge>
              <b-badge v-if="evo.absence_patient" variant="danger" class="mr-1">Falta paciente</b-badge>
              <b-badge v-if="evo.absence_professional" variant="warning">Falta profissional</b-badge>
            </div>
          </div>
          <div class="text-muted mb-2">
            {{ evo.agenda?.atendimento?.specialty?.name || "Sem especialidade" }} -
            {{ evo.agenda?.professional?.name || "Profissional não informado" }}
          </div>
          <div v-html="evo.desc"></div>
          <audio v-if="evo.audio_url" :src="evo.audio_url" class="w-100 mt-2" controls></audio>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="evolutionTotal"
            @change="fetchData"
          />
        </div>
      </section>

      <section class="mb-3">
        <h5 class="mb-3">Frequência mensal</h5>
        <div class="table-responsive">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Mês</th>
                <th>Realizadas</th>
                <th>Canceladas</th>
                <th>Faltas Paciente</th>
                <th>Faltas Profissional</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!attendanceByMonth.length">
                <td colspan="5" class="text-center">Sem dados de frequência.</td>
              </tr>
              <tr v-for="month in attendanceByMonth" :key="month.month">
                <td>{{ month.month }}</td>
                <td>{{ month.done }}</td>
                <td>{{ month.cancelled }}</td>
                <td>{{ month.absent_pac }}</td>
                <td>{{ month.absent_prof }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h5 class="mb-3">Documentos</h5>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Criado em</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!files.length">
                <td colspan="4" class="text-center">Sem arquivos no prontuário.</td>
              </tr>
              <tr v-for="file in files" :key="file.id">
                <td>{{ file.id }}</td>
                <td>{{ file.nome }}</td>
                <td>{{ formatDateTimeBR(file.created_at) }}</td>
                <td>
                  <a :href="file.url" target="_blank" rel="noopener noreferrer">Abrir</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.evolution-card {
  border-left: 4px solid #556ee6 !important;
}
</style>
